// Owner mailbox provisioning (#236): pure form <-> payload mapping. Credentials are
// write-only — an edit form starts with blank password fields, and a blank password is
// OMITTED from the payload (the server keeps the stored one). Only changed fields are
// sent on update, so the audit trail lists real changes.
import type { TenantMailboxInput, TenantMailboxRow } from "@/api";

export interface MailboxForm {
  imapHost: string;
  imapPort: string;
  imapEmail: string;
  imapFolder: string;
  /** Blank on edit = keep the stored password. */
  imapPassword: string;
  imapTlsAllowHostnameMismatch: boolean;
  imapTlsExpectedServername: string;
  smtpHost: string;
  smtpPort: string;
  smtpUser: string;
  /** Blank on edit = keep the stored password. */
  smtpPassword: string;
  smtpFrom: string;
  smtpFromName: string;
  smtpSecure: boolean;
  smtpTlsAllowHostnameMismatch: boolean;
}

export function emptyMailboxForm(): MailboxForm {
  return {
    imapHost: "",
    imapPort: "993",
    imapEmail: "",
    imapFolder: "INBOX",
    imapPassword: "",
    imapTlsAllowHostnameMismatch: false,
    imapTlsExpectedServername: "",
    smtpHost: "",
    smtpPort: "587",
    smtpUser: "",
    smtpPassword: "",
    smtpFrom: "",
    smtpFromName: "",
    smtpSecure: false,
    smtpTlsAllowHostnameMismatch: false,
  };
}

/** Prefill an edit form from a masked row — password fields stay blank (= keep). */
export function mailboxFormFromRow(row: TenantMailboxRow): MailboxForm {
  return {
    imapHost: row.imapHost,
    imapPort: String(row.imapPort),
    imapEmail: row.imapEmail,
    imapFolder: row.imapFolder,
    imapPassword: "",
    imapTlsAllowHostnameMismatch: row.imapTlsAllowHostnameMismatch,
    imapTlsExpectedServername: row.imapTlsExpectedServername ?? "",
    smtpHost: row.smtpHost ?? "",
    smtpPort: String(row.smtpPort),
    smtpUser: row.smtpUser ?? "",
    smtpPassword: "",
    smtpFrom: row.smtpFrom ?? "",
    smtpFromName: row.smtpFromName ?? "",
    smtpSecure: row.smtpSecure,
    smtpTlsAllowHostnameMismatch: row.smtpTlsAllowHostnameMismatch,
  };
}

const parsePort = (raw: string): number | null => {
  const v = Number(raw.trim());
  return Number.isInteger(v) && v >= 1 && v <= 65535 ? v : null;
};

/** i18n keys of validation problems; empty = form is submittable. */
export function mailboxFormErrors(form: MailboxForm, mode: "create" | "edit"): string[] {
  const errors: string[] = [];
  if (!form.imapHost.trim()) errors.push("mailbox.errImapHost");
  if (!form.imapEmail.trim()) errors.push("mailbox.errImapEmail");
  if (mode === "create" && !form.imapPassword) errors.push("mailbox.errImapPassword");
  if (parsePort(form.imapPort) === null) errors.push("mailbox.errImapPort");
  if (parsePort(form.smtpPort) === null) errors.push("mailbox.errSmtpPort");
  return errors;
}

export function buildCreatePayload(form: MailboxForm): TenantMailboxInput {
  return {
    imapHost: form.imapHost.trim(),
    imapPort: parsePort(form.imapPort) ?? undefined,
    imapEmail: form.imapEmail.trim(),
    imapFolder: form.imapFolder.trim() || "INBOX",
    imapPassword: form.imapPassword,
    imapTlsAllowHostnameMismatch: form.imapTlsAllowHostnameMismatch,
    imapTlsExpectedServername: form.imapTlsExpectedServername.trim() || null,
    smtpHost: form.smtpHost.trim() || null,
    smtpPort: parsePort(form.smtpPort) ?? undefined,
    smtpUser: form.smtpUser.trim() || null,
    ...(form.smtpPassword ? { smtpPassword: form.smtpPassword } : {}),
    smtpFrom: form.smtpFrom.trim() || null,
    smtpFromName: form.smtpFromName.trim() || null,
    smtpSecure: form.smtpSecure,
    smtpTlsAllowHostnameMismatch: form.smtpTlsAllowHostnameMismatch,
  };
}

/**
 * Diff the form against the row: only changed fields travel. Blank passwords are never
 * included (= keep the stored ones). Always carries `version` for the optimistic lock.
 */
export function buildUpdatePayload(form: MailboxForm, row: TenantMailboxRow): TenantMailboxInput {
  const payload: TenantMailboxInput = { version: row.version };
  const put = <K extends keyof TenantMailboxInput>(key: K, value: TenantMailboxInput[K], current: unknown) => {
    if (value !== current) payload[key] = value;
  };
  put("imapHost", form.imapHost.trim(), row.imapHost);
  put("imapPort", parsePort(form.imapPort) ?? row.imapPort, row.imapPort);
  put("imapEmail", form.imapEmail.trim(), row.imapEmail);
  put("imapFolder", form.imapFolder.trim() || "INBOX", row.imapFolder);
  put(
    "imapTlsAllowHostnameMismatch",
    form.imapTlsAllowHostnameMismatch,
    row.imapTlsAllowHostnameMismatch,
  );
  put(
    "imapTlsExpectedServername",
    form.imapTlsExpectedServername.trim() || null,
    row.imapTlsExpectedServername,
  );
  put("smtpHost", form.smtpHost.trim() || null, row.smtpHost);
  put("smtpPort", parsePort(form.smtpPort) ?? row.smtpPort, row.smtpPort);
  put("smtpUser", form.smtpUser.trim() || null, row.smtpUser);
  put("smtpFrom", form.smtpFrom.trim() || null, row.smtpFrom);
  put("smtpFromName", form.smtpFromName.trim() || null, row.smtpFromName);
  put("smtpSecure", form.smtpSecure, row.smtpSecure);
  put(
    "smtpTlsAllowHostnameMismatch",
    form.smtpTlsAllowHostnameMismatch,
    row.smtpTlsAllowHostnameMismatch,
  );
  if (form.imapPassword) payload.imapPassword = form.imapPassword;
  if (form.smtpPassword) payload.smtpPassword = form.smtpPassword;
  return payload;
}
